// 覚醒王・大暴れの床の波「紫の虚の割れ」(2026-10-20)。
//
// 作者の選択 = 床技7つの見本の D「紫の虚の割れ」(docs/research/2026-10-19-floor-fx-mihon.md・見本 そざい/concept/floor-fx-1019/awoken_wave_d.png)。
//   赤く光る割れ目が床を走り、割れ目から顔の虚と同じ紫の闇が噴く。
//   ① 先端 (当たりの帯・幅20) で床の石が割れてせり上がり、赤い割れ目が光り、紫の闇が噴き上がる。
//   ② 通った後ろは石がすぐ沈み、赤い割れ目が薄れ、紫の闇は散る (安全な所に立つ物を残さない)。
//   ③ 波が消えた後も、最後に通った所の石が沈み切るまで描く (端で急に消えない)。
// 当たり (場所・時間・速さ) は変えない＝ここは描くだけ。前の見た目 (2026-09-22 の絵) は DEV `?awokenWaveFx=old`。
// 縛り: 線 (stroke) で描かない・赤は割れ目の光だけ (弾けない合図)・白い効果線を描かない。
// 絵の部品 = Codex が1枚に描いた物を切り出した atlas (元絵 そざい/concept/awoken-wave-rift-1020/・切り出しの記録は
//   LUNAIRE-notes/assets/2026-10-20-awoken-wave-rift/)。動きはコード。絵が読めていない間は前の見た目で描く。
import type { ActiveHit } from './types'
import { glowMote, hash01 } from './fx_kit'

export type AwokenWaveFx = 'rift' | 'old'
let waveFx: AwokenWaveFx = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'rift'
  return new URLSearchParams(window.location.search).get('awokenWaveFx') === 'old' ? 'old' : 'rift'
})()
export function getAwokenWaveFx(): AwokenWaveFx { return waveFx }
export function setAwokenWaveFx(v: AwokenWaveFx): void { waveFx = v }

type Rect = readonly [number, number, number, number]
export const WAVE_RIFT_ATLAS: Record<string, Rect> = {
  rock_0: [0, 0, 103, 64], rock_1: [105, 0, 103, 65], rock_2: [210, 0, 92, 52], rock_3: [304, 0, 107, 74],
  strip_0: [0, 76, 259, 31], strip_1: [0, 109, 262, 30],
  plume0_0: [264, 109, 131, 75], plume1_0: [0, 186, 152, 118], plume2_0: [154, 186, 181, 97],
  chip_0: [337, 186, 25, 26], chip_1: [364, 186, 27, 16], chip_2: [393, 186, 15, 29], chip_3: [410, 186, 15, 14],
  chip_4: [427, 186, 20, 27], chip_5: [449, 186, 17, 14],
  wisp_0: [468, 186, 26, 28], wisp_1: [0, 306, 36, 32], wisp_2: [38, 306, 24, 32], wisp_3: [64, 306, 17, 25],
}
// 石の塊の絵の向き: 背の高い側が右 (+1) か左 (-1) か。先端側へ尖りを向けるのに使う。
const ROCK_TIP: Record<string, number> = { rock_0: -1, rock_1: 1, rock_2: -1, rock_3: 1 }
// 紫の闇の絵は右へ流れている。進む向きと逆へ (後ろへ) なびかせるので、右へ進む波では裏返す。
const PLUME_LEANS = 1

const SRC = '/sprites/fx/hazard/awoken_wave_rift_atlas.png'
let img: CanvasImageSource | null = null
let loading = false
export function preloadAwokenWaveRiftAtlas(): void {
  if (img || loading || typeof Image === 'undefined') return
  loading = true
  const im = new Image()
  im.onload = () => { img = im }
  im.onerror = () => { loading = false }
  im.src = SRC
}
/** テスト用 (偽の絵を差す)。 */
export function setAwokenWaveRiftAtlasForTest(v: CanvasImageSource | null): void { img = v }

const SPEED = 600          // WAVE.speedPxPerSec と同じ (年齢を距離から出す)
const SLOT = 30            // 石の塊を置く間隔 (px)
const ROCK_H = 34          // 石の塊の背丈 = 当たりの帯の高さ
const LINGER = 0.3         // 通った後に描き続ける秒数 (これより古い所は沈み切っている)
const FRONT_PLUME_H = 66

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }

// 部品1つを、下端中央 (ax, ay) を基準に描く。dw×dh はゲームの px。
function part(ctx: CanvasRenderingContext2D, im: CanvasImageSource, name: string,
  ax: number, ay: number, dw: number, dh: number, a: number, flip = false, rot = 0, sx0 = 0, sw?: number): void {
  if (a <= 0.01 || dw <= 0.5 || dh <= 0.5) return
  const r = WAVE_RIFT_ATLAS[name]!
  const w = sw ?? r[2]
  ctx.save()
  ctx.globalAlpha *= Math.min(1, a)
  ctx.translate(ax, ay)
  if (rot) ctx.rotate(rot)
  if (flip) ctx.scale(-1, 1)
  ctx.drawImage(im, r[0] + sx0, r[1], w, r[3], -dw / 2, -dh, dw, dh)
  ctx.restore()
}

// 石の立ち上がり (年齢 s 秒): 0.05 秒で突き上がって少し行き過ぎ、0.09 秒まで立ち、0.24 秒までに沈む。
//   立っている所が長いと、当たり (幅20) より広く危険に見える (10-20 撮影で後ろ 240px が立ったままだった)。
export function rockRise(age: number): number {
  if (age < 0) return 0
  if (age < 0.05) return easeOut(age / 0.05) * 1.08
  if (age < 0.08) return 1.08 - 0.08 * ((age - 0.05) / 0.03)
  if (age < 0.09) return 1
  return Math.max(0, 1 - (age - 0.09) / 0.15)
}

/** 波1本の形。front = 当たりの帯の中心 x、dist = 起点から帯の前端までの距離。 */
type Geom = { origin: number; floor: number; dir: number; dist: number; id: number }

/** 置く物の一覧 (テストで「帯の付近に収まる」を確かめる)。age は秒。 */
export function riftSlots(g: Geom, extraAge = 0): { x: number; age: number; k: number }[] {
  const out: { x: number; age: number; k: number }[] = []
  for (let k = 0; ; k += 1) {
    const d = 14 + k * SLOT + (hash01(g.id * 3.1 + k * 7.7) - 0.5) * 12
    if (d > g.dist) break
    const age = (g.dist - d) / SPEED + extraAge
    if (age > LINGER) continue
    out.push({ x: g.origin + g.dir * d, age, k })
  }
  return out
}

function drawRift(ctx: CanvasRenderingContext2D, im: CanvasImageSource, g: Geom, time: number, extraAge: number, live: boolean): void {
  const { floor, dir } = g
  const slots = riftSlots(g, extraAge)
  // ① 床の赤い割れ目 (帯の絵を薄く潰して床に敷く)。新しい所ほど明るい。
  for (const s of slots) {
    const a = clamp01(1 - s.age / 0.28)
    const strip = s.k % 2 ? 'strip_1' : 'strip_0'
    const r = WAVE_RIFT_ATLAS[strip]!
    const sw = 60, sx0 = Math.floor(hash01(g.id + s.k * 1.7) * (r[2] - sw))
    part(ctx, im, strip, s.x, floor + 4, SLOT + 12, 9, a * 0.95, dir < 0, 0, sx0, sw)
  }
  // ② 紫の闇: 1つおきの石から噴いて、0.3 秒で「噴き出し→高く→散る」と移って消える。
  for (const s of slots) {
    if (s.k % 2) continue
    const p = s.age / 0.3
    if (p >= 1) continue
    const name = p < 0.18 ? 'plume0_0' : p < 0.55 ? 'plume1_0' : 'plume2_0'
    const r = WAVE_RIFT_ATLAS[name]!
    const h = 46 * (0.85 + hash01(g.id + s.k * 2.3) * 0.3) * (name === 'plume2_0' ? 0.85 : name === 'plume0_0' ? 0.7 : 1)
    const a = p < 0.55 ? 0.9 : 0.9 * (1 - (p - 0.55) / 0.45)
    part(ctx, im, name, s.x - dir * 8, floor + 2, h * (r[2] / r[3]), h, a, dir * PLUME_LEANS > 0)
  }
  // ③ せり上がる石の塊 (先端側へ尖りを向ける)。沈む時は床へ縮みながら下がる。
  const rocks = ['rock_1', 'rock_3', 'rock_0', 'rock_2']
  for (const s of slots) {
    const rise = rockRise(s.age)
    if (rise <= 0.01) continue
    const name = rocks[(s.k + g.id) % rocks.length]!
    const r = WAVE_RIFT_ATLAS[name]!
    const h = ROCK_H * (0.85 + hash01(g.id * 1.3 + s.k) * 0.3)
    const w = h * (r[2] / r[3]) * 0.8
    part(ctx, im, name, s.x, floor + 3 + (1 - Math.min(1, rise)) * 4, w, h * rise, Math.min(1, rise * 1.6), ROCK_TIP[name]! !== dir)
  }
  // ④ 石のかけら: 生まれた瞬間に2つ跳ねる (0.35 秒)。
  for (const s of slots) {
    if (s.age > 0.35) continue
    for (let j = 0; j < 2; j += 1) {
      const seed = g.id * 5.3 + s.k * 11.1 + j * 3.7
      const vx = dir * (40 + hash01(seed) * 120) * (j ? 1 : -0.4)
      const vy = -(150 + hash01(seed + 1) * 140)
      const t = s.age
      const cx = s.x + vx * t, cy = floor - 4 + vy * t + 900 * t * t / 2
      if (cy > floor + 2) continue
      const name = `chip_${Math.floor(hash01(seed + 2) * 6)}`
      const r = WAVE_RIFT_ATLAS[name]!
      const sc = 0.32 + hash01(seed + 3) * 0.18
      part(ctx, im, name, cx, cy, r[2] * sc, r[3] * sc, 1 - t / 0.35, false, t * 9 * (j ? 1 : -1))
    }
  }
  if (!live) return
  // ⑤ 先端: 赤い割れ目の熱 (弾けない合図) と、噴き上がる紫の闇。前端 = 当たりの帯の前の縁。
  const lead = g.origin + g.dir * g.dist
  glowMote(ctx, lead - dir * 6, floor - 3, 30, 'rgba(255,40,40,0.9)', 0.55)
  glowMote(ctx, lead - dir * 6, floor - 1, 12, 'rgba(255,170,150,0.9)', 0.5)
  const ph = Math.floor(time * 14 + g.id) % 3
  const fname = ph === 0 ? 'plume0_0' : ph === 1 ? 'plume1_0' : 'plume2_0'
  const fr = WAVE_RIFT_ATLAS[fname]!
  const fh = FRONT_PLUME_H * (ph === 0 ? 0.8 : ph === 2 ? 0.9 : 1)
  part(ctx, im, fname, lead - dir * 14, floor + 2, fh * (fr[2] / fr[3]), fh, 0.95, dir * PLUME_LEANS > 0)
  // 先端の石 (いちばん新しい塊) を闇の手前に、いつも立てておく。
  const lr = WAVE_RIFT_ATLAS[dir > 0 ? 'rock_3' : 'rock_1']!
  const lh = ROCK_H * (1 + Math.sin(time * 40 + g.id) * 0.05)
  part(ctx, im, dir > 0 ? 'rock_3' : 'rock_1', lead - dir * 10, floor + 3, lh * (lr[2] / lr[3]) * 0.8, lh, 1, ROCK_TIP[dir > 0 ? 'rock_3' : 'rock_1']! !== dir)
  // 闇のちぎれ: 先端から後ろ上へ舞う (3つ)。
  for (let j = 0; j < 3; j += 1) {
    const seed = g.id * 9.1 + j * 4.3
    const t = (time * 2.2 + hash01(seed)) % 1
    const name = `wisp_${j}`
    const r = WAVE_RIFT_ATLAS[name]!
    const sc = 0.5 + hash01(seed + 1) * 0.3
    part(ctx, im, name, lead - dir * (10 + t * 46), floor - 20 - t * 34, r[2] * sc, r[3] * sc, (1 - t) * 0.85, dir > 0)
  }
}

// 波が消えた後に石を沈め切るための控え (id ごと)。
type Remnant = { g: Geom; seenAt: number }
const remnants = new Map<number, Remnant>()

/** 波1本を描く。絵が読めていない時は false (呼び手が前の見た目で描く)。 */
export function drawAwokenWaveRift(ctx: CanvasRenderingContext2D, hit: ActiveHit, time: number, floor: number, fade: number): boolean {
  if (!img) { preloadAwokenWaveRiftAtlas(); return false }
  const s = hit.spirit!
  const dir = Math.sign(hit.vx ?? 1) || 1
  const r = (hit.widthPx ?? 20) / 2
  const g: Geom = { origin: hit.x! - dir * s.traveledPx, floor, dir, dist: s.traveledPx + r, id: hit.id }
  remnants.set(hit.id, { g, seenAt: time })
  ctx.save()
  ctx.globalAlpha = fade
  drawRift(ctx, img, g, time, 0, true)
  ctx.restore()
  return true
}

/** 消えた波の残り (石が沈み切るまで)。毎コマ、波を描いた後に1回呼ぶ。 */
export function drawAwokenWaveRemnants(ctx: CanvasRenderingContext2D, time: number): void {
  if (!remnants.size || !img) return
  for (const [id, rem] of remnants) {
    const gone = time - rem.seenAt
    if (gone > LINGER || gone < 0) { remnants.delete(id); continue }  // 負 = やり直しで時間が戻った
    if (gone === 0) continue  // このコマは波そのものを描いた
    ctx.save()
    drawRift(ctx, img, rem.g, time, gone, false)
    ctx.restore()
  }
}
/** テスト用。 */
export function clearAwokenWaveRemnants(): void { remnants.clear() }
export function awokenWaveRemnantCount(): number { return remnants.size }

if (waveFx === 'rift' && typeof window !== 'undefined') preloadAwokenWaveRiftAtlas()
